import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ADMIN_ENDPOINTS } from '../config/api';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardActions,
  Grid,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';

const UserDashboard = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(ADMIN_ENDPOINTS.LIST, {
        headers: getAuthHeader(),
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch users');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (userId) => {
    navigate(`/admin/users/edit/${userId}`);
  };

  const handleAddNew = () => {
    navigate('/admin/users/add');
  };

  const handleDelete = async (userId) => {
      if (window.confirm('Are you sure you want to delete this user?')) {
        try {
          const response = await fetch(ADMIN_ENDPOINTS.DETAIL(userId), {
            method: 'DELETE',
            headers: getAuthHeader(),
          });
  
          if (response.ok) {
            setUsers(users.filter(user => user.id !== userId));
          } else {
            const errorData = await response.json();
            setError(errorData.error || 'Failed to delete user');
          }
        } catch (err) {
          setError('Something went wrong. Please try again.');
        }
      }
    };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return dayjs(dateString).format('DD/MM/YYYY, HH:mm');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Mobile card view for users
  const renderMobileUserCards = () => {
    return (
      <Grid container spacing={2} sx={{ width: '100%' }}>
        {users.map((user) => (
          <Grid item xs={12} key={user.id}>
            <Card sx={{ width: '100%' }}>
              <CardContent>
                <Typography variant="h6" component="div" gutterBottom>
                  {user.username}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom sx={{wordBreak: "break-word"}}>
                  {user.email}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom sx={{wordBreak: "break-word"}}>
                  {formatDate(user.createdAt)}
                </Typography>
              </CardContent>
              <Divider />
              <CardActions sx={{ justifyContent: 'flex-end' }}>
                <IconButton
                  size="small"
                  onClick={() => handleEdit(user.id)}
                  color="primary"
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(user.id)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  // Desktop table view for users
  const renderDesktopUserTable = () => {
    return (
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="users table">
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Created at</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow
                key={user.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component="th" scope="row" sx={{minWidth: 160}}>
                  {user.username}
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{formatDate(user.createdAt)}</TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(user.id)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(user.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 0, mb: 4, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'center' : 'center', 
        mb: 3, 
        mt: 1,
        width: '100%'
      }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: isMobile ? 2 : 0, textAlign: isMobile ? 'center' : 'left' }}>
          Registered Users
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          gap: 2,
          width: isMobile ? '100%' : 'auto',
          alignItems: 'center'
        }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            fullWidth={isMobile}
          >
            Add New User
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, width: '100%' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ width: '100%', flex: 1 }}>
          {isMobile ? renderMobileUserCards() : renderDesktopUserTable()}
        </Box>
      )}
    </Container>
  );
};

export default UserDashboard; 